import { useEffect, useRef, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Stack,
  Typography,
  Chip,
  CircularProgress,
  useTheme
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ArchiveIcon from "@mui/icons-material/Archive";
import CancelIcon from "@mui/icons-material/Cancel";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import Tooltip from "@mui/material/Tooltip";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { useTesterIdentity } from "./TesterIdentityProvider";
import {
  sendBugMessage,
  closeBug,
  approveProposal,
  cancelFix
} from "./api";
import { getStatusDisplay } from "./statusMap";
import useFixLocks from "./useFixLocks";

const markdownComponents = {
  p: ({ children }) => (
    <Typography
      variant="body2"
      color="inherit"
      sx={{ m: 0, mb: 0.5, "&:last-child": { mb: 0 } }}
    >
      {children}
    </Typography>
  ),
  h1: ({ children }) => (
    <Typography variant="subtitle1" color="inherit" sx={{ mt: 0.5, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography variant="subtitle1" color="inherit" sx={{ mt: 0.5, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="subtitle2" color="inherit" sx={{ mt: 0.5, mb: 0.5 }}>
      {children}
    </Typography>
  ),
  strong: ({ children }) => <strong>{children}</strong>,
  em: ({ children }) => <em>{children}</em>,
  ul: ({ children }) => (
    <ul style={{ margin: "4px 0", paddingLeft: 20 }}>{children}</ul>
  ),
  ol: ({ children }) => (
    <ol style={{ margin: "4px 0", paddingLeft: 20 }}>{children}</ol>
  ),
  li: ({ children }) => (
    <li>
      <Typography variant="body2" color="inherit" component="span">
        {children}
      </Typography>
    </li>
  ),
  code: ({ inline, children }) =>
    inline ? (
      <code
        style={{
          fontFamily: "monospace",
          fontSize: "0.85em",
          padding: "1px 4px",
          borderRadius: 3,
          background: "rgba(0,0,0,0.08)"
        }}
      >
        {children}
      </code>
    ) : (
      <pre
        style={{
          fontFamily: "monospace",
          fontSize: "0.85em",
          padding: 8,
          borderRadius: 4,
          background: "rgba(0,0,0,0.08)",
          overflowX: "auto",
          margin: "4px 0"
        }}
      >
        <code>{children}</code>
      </pre>
    ),
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "inherit", textDecoration: "underline" }}
    >
      {children}
    </a>
  )
};

const MessageBubble = ({ message }) => {
  const { palette } = useTheme();
  const isUser = message.role === "user";
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start"
      }}
    >
      <Box
        sx={{
          maxWidth: "85%",
          px: 1.5,
          py: 1.5,
          borderRadius: 2,
          bgcolor: isUser ? palette.primary.main : palette.backgroundGray.main,
          color: isUser ? palette.primary.contrastText : palette.text.primary,
          wordBreak: "break-word"
        }}
      >
        {isUser ? (
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap" }}
            color="inherit"
          >
            {message.content}
          </Typography>
        ) : (
          <Markdown
            components={markdownComponents}
            remarkPlugins={[remarkGfm]}
          >
            {message.content || ""}
          </Markdown>
        )}
      </Box>
    </Box>
  );
};

const otherHoldersLabel = (otherLocks) => {
  const names = otherLocks
    .map((lock) => lock.testerName)
    .filter(Boolean);
  if (names.length === 0) return "Another tester";
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

const BugThread = ({ bugId, onBack, onSelectBug }) => {
  const { internalDb, getIdToken } = useTesterIdentity();
  const { ownLock, otherLocks, canForceUnlock } = useFixLocks();
  const [bug, setBug] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [verdictSubmitting, setVerdictSubmitting] = useState(null);
  const [approving, setApproving] = useState(false);
  const [confirmCrossTester, setConfirmCrossTester] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const messagesScrollRef = useRef(null);
  const messageCount = Array.isArray(bug?.messages) ? bug.messages.length : 0;

  useEffect(() => {
    const node = messagesScrollRef.current;
    if (!node || !messageCount) {
      return;
    }
    node.scrollTop = node.scrollHeight;
  }, [bugId, messageCount]);

  useEffect(() => {
    if (!internalDb || !bugId) {
      return undefined;
    }
    const unsubscribe = onSnapshot(
      doc(internalDb, "tester-bugs", bugId),
      (snap) => {
        setBug(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoaded(true);
      },
      () => {
        setBug(null);
        setLoaded(true);
      }
    );
    return unsubscribe;
  }, [internalDb, bugId]);

  const handleSend = async () => {
    const trimmed = draft.trim();
    if (!trimmed) {
      return;
    }
    setSending(true);
    setError(null);
    try {
      await sendBugMessage({ bugId, message: trimmed, getIdToken });
      setDraft("");
    } catch (err) {
      setError(err.message || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleMarkResolved = async () => {
    setVerdictSubmitting("pass");
    setError(null);
    try {
      await closeBug({ bugId, verdict: "pass", getIdToken });
    } catch (err) {
      setError(err.message || "Failed to close");
    } finally {
      setVerdictSubmitting(null);
    }
  };

  const submitApprove = async ({ confirmConflict }) => {
    setApproving(true);
    setError(null);
    try {
      await approveProposal({ bugId, confirmConflict, getIdToken });
    } catch (err) {
      if (err.code === "other_tester_fix_running" && !confirmConflict) {
        setConfirmCrossTester(true);
      } else if (err.code === "tester_lock_held") {
        setError(
          `You already have a fix running on Bug #${err.heldBugId || "?"}. Please wait for it to finish or cancel it first.`
        );
      } else {
        setError(err.message || "Failed to approve");
      }
    } finally {
      setApproving(false);
    }
  };

  const handleApprove = () => submitApprove({ confirmConflict: false });
  const handleConfirmCrossTesterApprove = async () => {
    setConfirmCrossTester(false);
    await submitApprove({ confirmConflict: true });
  };

  const handleCancelFix = async () => {
    setCancelling(true);
    setError(null);
    try {
      await cancelFix({ bugId: ownLock?.bugId || null, getIdToken });
    } catch (err) {
      if (err.code !== "no_lock") {
        setError(err.message || "Failed to cancel");
      }
    } finally {
      setCancelling(false);
    }
  };

  const handleStillBroken = async () => {
    setVerdictSubmitting("fail");
    setError(null);
    try {
      await sendBugMessage({
        bugId,
        message: "Still broken — needs another look.",
        getIdToken
      });
    } catch (err) {
      setError(err.message || "Failed to send");
    } finally {
      setVerdictSubmitting(null);
    }
  };

  const handleTryAgain = async () => {
    setVerdictSubmitting("retry");
    setError(null);
    try {
      await sendBugMessage({
        bugId,
        message: "Please try the fix again.",
        getIdToken
      });
    } catch (err) {
      setError(err.message || "Failed to send");
    } finally {
      setVerdictSubmitting(null);
    }
  };

  const handleCloseConversation = async () => {
    setVerdictSubmitting("close");
    setError(null);
    try {
      await closeBug({
        bugId,
        verdict: "not-a-bug",
        note: "Closed by tester.",
        getIdToken
      });
    } catch (err) {
      setError(err.message || "Failed to close");
    } finally {
      setVerdictSubmitting(null);
    }
  };

  if (!loaded) {
    return (
      <Box sx={{ p: 2, textAlign: "center" }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!bug) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="textSecondary">
          Bug not found.
        </Typography>
      </Box>
    );
  }

  const messages = Array.isArray(bug.messages) ? bug.messages : [];
  const { label, color } = getStatusDisplay(bug.status);
  const latestFix = Array.isArray(bug.fixAttempts) ? bug.fixAttempts[bug.fixAttempts.length - 1] : null;
  const testUrl = latestFix?.testUrl;
  const ownLockOnThisBug = ownLock && ownLock.bugId === bugId;
  const ownLockBlocksApprove = ownLock && ownLock.bugId !== bugId;
  const otherTesterLabel = otherLocks.length > 0 ? otherHoldersLabel(otherLocks) : null;
  const otherHeldBugId = otherLocks[0]?.bugId || null;
  const ownLockBugLabel = ownLock
    ? `Bug #${ownLock.bugId}${ownLock.bugTitle ? `: ${ownLock.bugTitle}` : ""}`
    : null;
  const lastMessage = messages[messages.length - 1];
  const lastMessageFromAgent = Boolean(lastMessage) && lastMessage.role !== "user";
  const lastMessageIsFreshProposal =
    lastMessageFromAgent && Boolean(lastMessage.proposal);

  return (
    <Stack
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      spacing={0}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          borderBottom: 1,
          borderColor: "divider"
        }}
      >
        <IconButton size="small" onClick={onBack}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {bug.description}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <Chip label={label} size="small" color={color} />
            {testUrl ? (
              <Typography variant="caption" color="textSecondary" noWrap>
                Live on {testUrl}
              </Typography>
            ) : null}
          </Box>
        </Box>
        {bug.status !== "closed" ? (
          <Tooltip title="Close conversation (not a bug)">
            <span>
              <IconButton
                size="small"
                onClick={handleCloseConversation}
                disabled={Boolean(verdictSubmitting)}
              >
                {verdictSubmitting === "close" ? (
                  <CircularProgress size={16} />
                ) : (
                  <ArchiveIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        ) : null}
      </Box>
      {ownLock ? (
        <Alert
          severity="info"
          icon={<CircularProgress size={16} />}
          action={
            <Stack direction="row" spacing={1}>
              {!ownLockOnThisBug && onSelectBug ? (
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => onSelectBug(ownLock.bugId)}
                >
                  View
                </Button>
              ) : null}
              <Button
                size="small"
                color="inherit"
                startIcon={
                  cancelling ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : canForceUnlock ? (
                    <LockOpenIcon fontSize="small" />
                  ) : (
                    <CancelIcon fontSize="small" />
                  )
                }
                onClick={handleCancelFix}
                disabled={cancelling}
              >
                {canForceUnlock ? "Force unlock" : "Cancel"}
              </Button>
            </Stack>
          }
          sx={{ borderRadius: 0 }}
        >
          <Typography variant="caption">
            Fix in progress on {ownLockBugLabel}
            {canForceUnlock
              ? " — this has been running a while. Only force unlock if the fix appears stuck (this may cause conflicts)."
              : ""}
          </Typography>
        </Alert>
      ) : null}
      {!ownLock && otherTesterLabel ? (
        <Alert severity="warning" sx={{ borderRadius: 0 }}>
          <Typography variant="caption">
            {otherTesterLabel} is running a fix on Bug #{otherHeldBugId || "?"} — currently deploying. Proceed with caution to avoid conflicts.
          </Typography>
        </Alert>
      ) : null}
      <Box ref={messagesScrollRef} sx={{ flex: 1, overflowY: "auto", p: 1.5 }}>
        <Stack spacing={1}>
          {messages.map((message, idx) => (
            <MessageBubble key={idx} message={message} />
          ))}
          {messages.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No messages yet.
            </Typography>
          ) : null}
        </Stack>
      </Box>
      <Box sx={{ p: 1, borderTop: 1, borderColor: "divider" }}>
        {bug.status === "awaiting-approval" && lastMessageIsFreshProposal ? (
          <Box sx={{ mb: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={
                approving ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
              onClick={handleApprove}
              disabled={approving}
              fullWidth
            >
              {ownLockBlocksApprove ? "Approve plan (will queue)" : "Approve plan"}
            </Button>
            {ownLockBlocksApprove ? (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5, display: "block", textAlign: "center" }}
              >
                Approving will queue this behind your current fix on {ownLockBugLabel}. It&apos;ll start automatically when that one finishes.
              </Typography>
            ) : (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 0.5, display: "block", textAlign: "center" }}
              >
                or reply below with any changes
              </Typography>
            )}
          </Box>
        ) : null}
        {bug.status === "ready-to-retest" ? (
          <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={
                verdictSubmitting === "pass" ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CheckCircleIcon />
                )
              }
              onClick={handleMarkResolved}
              disabled={Boolean(verdictSubmitting)}
              fullWidth
            >
              Looks good
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={
                verdictSubmitting === "fail" ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <ReplayIcon />
                )
              }
              onClick={handleStillBroken}
              disabled={Boolean(verdictSubmitting)}
              fullWidth
            >
              Still broken
            </Button>
          </Box>
        ) : null}
        {bug.status === "fix-failed" && lastMessageFromAgent ? (
          <Box sx={{ mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={
                verdictSubmitting === "retry" ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <ReplayIcon />
                )
              }
              onClick={handleTryAgain}
              disabled={Boolean(verdictSubmitting) || ownLockBlocksApprove}
              fullWidth
            >
              Try again
            </Button>
          </Box>
        ) : null}
        {error ? (
          <Typography variant="caption" color="error" sx={{ mb: 0.5, display: "block" }}>
            {error}
          </Typography>
        ) : null}
        <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
          <TextField
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Reply…"
            multiline
            maxRows={4}
            size="small"
            fullWidth
            disabled={sending || bug.status === "closed"}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={sending || !draft.trim() || bug.status === "closed"}
          >
            {sending ? <CircularProgress size={20} /> : <SendIcon />}
          </IconButton>
        </Box>
      </Box>
      <Dialog open={confirmCrossTester} onClose={() => setConfirmCrossTester(false)}>
        <DialogTitle>Another fix is running</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {otherTesterLabel || "Another tester"} is running a fix right now. Files may conflict. Proceed anyway?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCrossTester(false)}>Wait</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmCrossTesterApprove}
          >
            Proceed
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default BugThread;
